import _ from "lodash";
import React from "react";
import { useDebounce } from 'use-debounce';
import { GridMessageType, GridMessageFilters } from "../types/MessageType";
import { SearchUserMessagesRequest } from "../services/messages/types";
import { useLazySearchUserMessagesQuery } from "../services/messages";
import moment from "moment";

export type MessageManagementContextValues = {
    clear: () => void
    data: GridMessageType[]
    error: Error | string | null
    hasMore: boolean
    isFetching: boolean
    isLoading: boolean
    isNextPageLoading: boolean
    isNewRecords: boolean
    isRowLoaded: boolean
    limit: number
    loadMore: () => void
    newRecordsSinceCache: number
    openReport: (documentId: string, fileName?: string | null) => void
    page: number
    pagination: Pagination
    setPageLimit: (v: number) => void
    setSearchTerm: (v: string) => void
    sort: string
    sortTable: (property: string) => void
    totalCount: number
    totalPages: number 
};

export type MessageManagementProviderProps = {
    children: JSX.Element
    defaultPageLimit?: number
    filter?: GridMessageFilters | null
    isReady?: boolean
    userId: string
};

const MessageManagementContext = React.createContext<MessageManagementContextValues | null>(null);

export const useMessageManagementContext = () => React.useContext<MessageManagementContextValues | null>(MessageManagementContext);

export const MessageManagementDefaultFilter : GridMessageFilters = {
    direction: 2,
    showDone: false,
    showHiddenByUser: false,
    masterAccountId: null,
    sentDateFrom: moment().startOf('year').format("YYYY-MM-DD"),
    sentDateTo: null,
    deadlineDateFrom: null,
    deadlineDateTo: null,
    reminderDateFrom: null,
    reminderDateTo: null,
    senderId: null,
    recipientId: null,
    subject: null,
    type: null,
    category: null,
    priorities: [],
    userPriorities: []
};

const MessageManagementProvider = ({ 
    children,
    defaultPageLimit = 54,
    filter = null,
    isReady = true,
    userId
} : Readonly<MessageManagementProviderProps>) => {
    const [sort, setSort] = React.useState<string>("SentStatus,SentOnDate+DESC");
    const [pageLimit, setPageLimit] = React.useState<number>(defaultPageLimit);
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [searchTermValue] = useDebounce(searchTerm, 300);
    const [isClearing, setIsClearing] = React.useState<boolean>(false);
    const [cacheKey, setCacheKey] = React.useState<number>(new Date().valueOf());
    const [isLoadingState, setIsLoadingState] = React.useState<boolean>(false);
    const [load, { data, isLoading: isLoadingData, isFetching, ...loadResults }] = useLazySearchUserMessagesQuery();

    // extract the results of the last load to our report search
    // note: we need to have a default set of data in-case we have an undefined response
    const { pagination, results, newRecordsSinceCache } = data || { pagination: { page: 1, pageSize: pageLimit, totalCount: 0, totalPages: 1 }, results: [], newRecordsSinceCache: 0 };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const isLoading = isLoadingData === true;
    const isNextPageLoading = isFetching === true || isLoadingData === true || isLoadingState === true;
    
    const buildRequest = (cacheKey: number, page: number) : SearchUserMessagesRequest => {

        return filter
            ? { 
                cacheKey,
                userId,
                request: {
                    ...filter,
                    page,
                    limit: pageLimit,
                    sort,
                    searchTerm: searchTermValue
                }
            }
            : {
                cacheKey,
                userId,
                request: {
                    ...MessageManagementDefaultFilter,
                    page,
                    limit: pageLimit,
                    sort,
                    searchTerm: searchTermValue
                }
            }
    };

    const clear = () => {
        if (isNextPageLoading || !isReady || !userId) {
            return;
        }

        
        const newCacheKey = new Date().valueOf();
        const queryOptions = buildRequest(newCacheKey, 1);

        setIsClearing(_ => true);
        setIsLoadingState(_ => true);
        load(queryOptions).unwrap()
            .then(() => {
                setCacheKey(_prev => newCacheKey);
            })
            .finally(() => {
                setIsClearing(_ => false);
                setIsLoadingState(_ => false);
            });
    };

    const isRowLoaded = (index: number) : boolean => {
        return index < results.length || results[index];
    }

    const loadMore = () => {
        if (isNextPageLoading || !hasMore || !isReady || !userId) {
            return;
        }

        setIsLoadingState(_ => true);
        load(buildRequest(cacheKey, (page + 1))).unwrap().then(() => {
            setIsLoadingState(_ => false);
        });
    };

    const sortTable = (property: string) => {
        // we want the sort to initially be descending ??
        let newDirection: string | null = "";
        let [sortProperty, direction] = sort.split("+");

        // if the current property is already selected, reverse the sort direction
        if (property === sortProperty) {
            newDirection = direction === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
        clear();
    };

    React.useEffect(clear, [ searchTermValue, sort, userId, filter, isReady ]);

    return (
        <MessageManagementContext.Provider value={{
            ...loadResults,
            clear,
            data: isClearing ? [] : results,
            hasMore,
            isFetching,
            isLoading,
            isNextPageLoading,
            isNewRecords: newRecordsSinceCache > 0,
            isRowLoaded,
            limit: pageLimit,
            loadMore: isNextPageLoading ? () => {} : loadMore, 
            newRecordsSinceCache,
            page,
            pagination,
            searchTerm,
            setPageLimit,
            setSearchTerm,
            sort,
            sortTable,
            totalCount: !hasMore && results && results.length >= 0 ? results.length : totalCount, // this should fix bottom row loading renders
            totalPages
        }}>
            {children}
        </MessageManagementContext.Provider>
    );
};

export default MessageManagementProvider;